import { resolveActionCards } from './resolveActionCards';
import { pushDialog } from '@src/components/Dialog/Dialog';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { showDialog, showToast } from '@src/components';
import { ExclusionEditor } from '@src/pages/RandomizerPage/ExclusionEditor';
import {
  excludedFor,
  isMutual,
  isDrawImpossible,
  setExclusionsFor,
  setMutual,
  type DrawDraft,
} from '@src/pages/RandomizerPage/draft';
import { santaClient } from '@src/state/client';

/** Adapt stored card IDs to the same editor and draft rules used by quick draw. */
export function useBoxExclusions(
  info: BoxInfo,
  source: CardInfo[] | (() => Promise<CardInfo[]>),
  onChanged: () => Promise<void> | void,
) {
  const { t } = useTranslation();
  return async (card: CardInfo, nested = false) => {
    const cards = await resolveActionCards(source);
    if (!cards) return;
    if (!info.isAdmin || info.isActiveDraw || info.box.isArchived) return;
    const subject = String(card.id);
    const people = cards.filter((person) => Boolean(person.username));
    let draft: DrawDraft = {
      gameName: info.box.name,
      names: people.map((person) => String(person.id)),
      exclusionsByName: Object.fromEntries(
        people.map((person) => [String(person.id), (person.exclusions || []).map(String)]),
      ),
    };
    const saved = structuredClone(draft.exclusionsByName);
    let query = '';
    let saving = false;
    let firstRender = true;
    function render() {
      const impossible = isDrawImpossible(draft, Boolean(info.box.useCircleDraw));
      const display = nested && firstRender ? pushDialog : showDialog;
      firstRender = false;
      display({
        title: t('randomizer.exclusions.editorTitle', { name: card.username }),
        children: (
          <ExclusionEditor
            options={people
              .filter((person) => person.id !== card.id)
              .map((person) => ({
                id: String(person.id),
                name: person.username || '',
                selected: excludedFor(draft, subject).includes(String(person.id)),
                mutual: isMutual(draft, subject, String(person.id)),
              }))}
            subtitle={t('randomizer.exclusions.editorSubtitle', { name: card.username })}
            query={query}
            onQueryChange={(value) => {
              if (saving) return;
              query = value;
              render();
            }}
            searchLabel={t('randomizer.exclusions.search')}
            peopleLabel={t('randomizer.exclusions.people')}
            mutualLabel={t('randomizer.exclusions.mutual')}
            mutualHint={t('randomizer.exclusions.mutualHint')}
            error={impossible ? 'randomizer.impossible' : undefined}
            onToggle={(id, selected) => {
              if (saving) return;
              const existing = excludedFor(draft, subject);
              draft = setExclusionsFor(
                draft,
                subject,
                selected ? [...existing, id] : existing.filter((value) => value !== id),
              );
              render();
            }}
            onToggleMutual={(id, mutual) => {
              if (saving) return;
              draft = setMutual(draft, subject, id, mutual);
              render();
            }}
            testId="exclusion-editor"
          />
        ),
        closeLabel: t('dialog.close'),
        cancel: t('randomizer.exclusions.cancel'),
        submit: t('randomizer.exclusions.save'),
        submitDisabled: impossible,
        onSubmit: async () => {
          if (isDrawImpossible(draft, Boolean(info.box.useCircleDraw)))
            throw new Error('impossible draw');
          saving = true;
          try {
            for (const person of people) {
              const id = String(person.id);
              const next = excludedFor(draft, id);
              const previous = saved[id] || [];
              if (
                next.length === previous.length &&
                next.every((value) => previous.includes(value))
              )
                continue;
              const res = await santaClient.setBoxCardExclusions({
                key: info.box.key,
                cardId: person.id,
                cardIds: next.map(Number),
              });
              if (!res.ok) throw new Error('exclusions save failed');
              saved[id] = [...next];
            }
            await onChanged();
          } catch (error) {
            await onChanged();
            showToast(t('newBox.saveFailed'));
            throw error;
          } finally {
            saving = false;
          }
        },
        testId: 'exclusion-dialog',
        mobileFullScreen: true,
      });
    }
    render();
  };
}
